import React, { useState, useEffect } from 'react';
import { StudentFee, PaymentFormData, PaymentFormErrors } from '../../types/fees';
import { paymentsApi } from '../../services/feesApi';
import { toast } from 'react-toastify';
import './PaymentModal.css';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  fee: StudentFee | null;
  onPaymentSuccess: () => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  fee,
  onPaymentSuccess
}) => {
  const [formData, setFormData] = useState<PaymentFormData>({
    feeId: 0,
    amountPaid: '',
    paymentDate: new Date().toISOString().split('T')[0],
    receiptVoucherNumber: '',
    notes: '',
    paymentMethod: 'CASH',
    transactionNumber: '',
    transactionDate: '',
    bankName: ''
  });

  const [errors, setErrors] = useState<PaymentFormErrors>({});
  const [loading, setLoading] = useState(false);
  const [generatingVoucher, setGeneratingVoucher] = useState(false);

  useEffect(() => {
    if (fee && isOpen) {
      setFormData({
        feeId: fee.id,
        amountPaid: fee.amount.toString(),
        paymentDate: new Date().toISOString().split('T')[0],
        receiptVoucherNumber: '',
        notes: '',
        paymentMethod: 'CASH',
        transactionNumber: '',
        transactionDate: '',
        bankName: ''
      });
      setErrors({});
      generateVoucherNumber();
    }
  }, [fee, isOpen]);

  const generateVoucherNumber = async () => {
    try {
      setGeneratingVoucher(true);
      const response = await paymentsApi.generateVoucherNumber();
      setFormData(prev => ({
        ...prev,
        receiptVoucherNumber: response.data.receiptVoucherNumber
      }));
    } catch (error: any) {
      console.error('Error generating voucher number:', error);
      toast.error('Failed to generate voucher number');
    } finally {
      setGeneratingVoucher(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: PaymentFormErrors = {};

    if (!formData.amountPaid || parseFloat(formData.amountPaid) <= 0) {
      newErrors.amountPaid = 'Amount paid must be greater than 0';
    }

    if (!formData.paymentDate) {
      newErrors.paymentDate = 'Payment date is required';
    }

    if (!formData.receiptVoucherNumber.trim()) {
      newErrors.receiptVoucherNumber = 'Receipt voucher number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const paymentData: any = {
        feeId: formData.feeId,
        amountPaid: parseFloat(formData.amountPaid),
        paymentDate: formData.paymentDate,
        receiptVoucherNumber: formData.receiptVoucherNumber.trim(),
        notes: formData.notes.trim() || undefined,
        paymentMethod: formData.paymentMethod || 'CASH'
      };

      if (formData.paymentMethod !== 'CASH') {
        if (formData.transactionNumber.trim()) paymentData.transactionNumber = formData.transactionNumber.trim();
        if (formData.transactionDate) paymentData.transactionDate = formData.transactionDate;
        if (formData.bankName.trim()) paymentData.bankName = formData.bankName.trim();
      }

      const recordResponse = await paymentsApi.create(paymentData);
      
      toast.success('Payment recorded successfully!');

      const paymentId = recordResponse.data?.paymentId;
      const voucherNumber = recordResponse.data?.receiptVoucherNumber || formData.receiptVoucherNumber;

      if (paymentId && voucherNumber) {
        try {
          await paymentsApi.downloadReceipt(paymentId, voucherNumber);
        } catch (downloadError) {
          console.error('Error downloading receipt:', downloadError);
          toast.warn('Payment saved, but receipt download failed. Please try again from the payments list.');
        }
      }
      onPaymentSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error recording payment:', error);

      const parsedError = (() => {
        if (!error) return null;
        if (typeof error === 'string') {
          try {
            return JSON.parse(error);
          } catch {
            return { error };
          }
        }
        return error;
      })();

      if (parsedError?.code === 'FEE_ALREADY_PAID') {
        toast.info('This fee is already marked as paid.');
        onPaymentSuccess();
        onClose();
        return;
      }

      if (parsedError?.field) {
        setErrors({ [parsedError.field]: parsedError.error });
      } else {
        setErrors({ general: parsedError?.error || 'Failed to record payment' });
      }
      
      toast.error(parsedError?.error || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof PaymentFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!isOpen || !fee) {
    return null;
  }

  return (
    <div className="payment-modal-overlay" onClick={onClose}>
      <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="payment-modal-header">
          <h2>Record Payment</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="payment-modal-body">
          {/* Fee Information */}
          <div className="fee-info-section">
            <h3>Fee Details</h3>
            <div className="fee-info-grid">
              <div className="fee-info-item">
                <label>Student:</label>
                <span>{fee.studentName}</span>
              </div>
              <div className="fee-info-item">
                <label>Description:</label>
                <span>{fee.feeDescription}</span>
              </div>
              <div className="fee-info-item">
                <label>Amount Due:</label>
                <span className="amount-due">{formatCurrency(fee.amount)}</span>
              </div>
              <div className="fee-info-item">
                <label>Due Date:</label>
                <span className={fee.status === 'overdue' ? 'overdue-date' : ''}>
                  {formatDate(fee.dueDate)}
                </span>
              </div>
              <div className="fee-info-item">
                <label>Status:</label>
                <span className={`status-badge status-${fee.status}`}>
                  {fee.status.charAt(0).toUpperCase() + fee.status.slice(1)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <form onSubmit={handleSubmit} className="payment-form">
            <h3>Payment Information</h3>

            {errors.general && (
              <div className="error-message general-error">
                {errors.general}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="amountPaid">
                  Amount Paid <span className="required">*</span>
                </label>
                <div className={`currency-input ${errors.amountPaid ? 'error' : ''} ${formData.amountPaid ? 'has-value' : ''}`}>
                  <span className="currency-symbol">AED</span>
                  <input
                    type="number"
                    id="amountPaid"
                    value={formData.amountPaid}
                    onChange={(e) => handleInputChange('amountPaid', e.target.value)}
                    step="0.01"
                    min="0"
                    disabled={loading}
                    placeholder="Enter amount"
                  />
                </div>
                {errors.amountPaid && (
                  <div className="error-message">{errors.amountPaid}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="paymentDate">
                  Payment Date <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="paymentDate"
                  value={formData.paymentDate}
                  onChange={(e) => handleInputChange('paymentDate', e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className={errors.paymentDate ? 'error' : ''}
                  disabled={loading}
                />
                {errors.paymentDate && (
                  <div className="error-message">{errors.paymentDate}</div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="receiptVoucherNumber">
                Receipt Voucher Number <span className="required">*</span>
              </label>
              <div className="voucher-input-group">
                <input
                  type="text"
                  id="receiptVoucherNumber"
                  value={formData.receiptVoucherNumber}
                  onChange={(e) => handleInputChange('receiptVoucherNumber', e.target.value)}
                  placeholder="RCP-YYYYMMDD-XXXX"
                  className={errors.receiptVoucherNumber ? 'error' : ''}
                  disabled={loading || generatingVoucher}
                />
                <button
                  type="button"
                  className="generate-voucher-btn"
                  onClick={generateVoucherNumber}
                  disabled={loading || generatingVoucher}
                  title="Generate new voucher number"
                >
                  {generatingVoucher ? '⏳' : '🔄'}
                </button>
              </div>
              {errors.receiptVoucherNumber && (
                <div className="error-message">{errors.receiptVoucherNumber}</div>
              )}
              <div className="field-help">
                A unique voucher number will be generated automatically
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="notes">Payment Notes</label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Optional notes about this payment..."
                rows={3}
                maxLength={1000}
                disabled={loading}
              />
              <div className="field-help">
                {formData.notes.length}/1000 characters
              </div>
            </div>

            <h3>Payment Method</h3>

            <div className="form-group">
              <label htmlFor="paymentMethod">Method</label>
              <select
                id="paymentMethod"
                value={formData.paymentMethod}
                onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                disabled={loading}
              >
                <option value="CASH">Cash</option>
                <option value="CARD">Card</option>
                <option value="ONLINE PAYMENT">Online Payment</option>
                <option value="CHEQUE">Cheque</option>
                <option value="BANK TRANSFER">Bank Transfer</option>
              </select>
            </div>

            {formData.paymentMethod !== 'CASH' && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="transactionNumber">Transaction No.</label>
                  <input
                    type="text"
                    id="transactionNumber"
                    value={formData.transactionNumber}
                    onChange={(e) => handleInputChange('transactionNumber', e.target.value)}
                    placeholder="Transaction reference number"
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="transactionDate">Transaction Date</label>
                  <input
                    type="date"
                    id="transactionDate"
                    value={formData.transactionDate}
                    onChange={(e) => handleInputChange('transactionDate', e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    disabled={loading}
                  />
                </div>
              </div>
            )}

            {formData.paymentMethod !== 'CASH' && (
              <div className="form-group">
                <label htmlFor="bankName">Bank Name</label>
                <input
                  type="text"
                  id="bankName"
                  value={formData.bankName}
                  onChange={(e) => handleInputChange('bankName', e.target.value)}
                  placeholder="Bank name"
                  disabled={loading}
                />
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || generatingVoucher}
              >
                {loading ? (
                  <>
                    <span className="loading-spinner"></span>
                    Recording Payment...
                  </>
                ) : (
                  <>
                    💳 Record Payment
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
